import {
    Action,
    ActionType,
    EarlyBirdRuleWithUiId,
} from "app/pages/EarlyBirdOfferBuilder/actionTypes";
import { EarlyBirdOfferBuilderState } from "app/pages/EarlyBirdOfferBuilder/modules/EarlyBirdOfferBuilder/state";

const emptyLookups: EarlyBirdOfferBuilderState["lookups"] = {
    countries: [],
    schools: [],
    bookingSystems: [],
    courses: [],
    accommodations: [],
    addOns: [],
    accommodationRooms: [],
    addOnTypes: [],
    accommodationTypes: [],
    admissionRegions: [],
    agents: [],
};

const startingState: EarlyBirdOfferBuilderState = {
    rules: [
        {
            name: "",
            earlyBirdEndDate: "",
            include: [],
            exclude: [],
        },
    ],
    year: new Date().getFullYear(),
    divisionId: 0,
    lookups: emptyLookups,
    editingConditionUiId: null,
    dirty: false,
};

function updateConditionInRule(
    rule: EarlyBirdRuleWithUiId,
    lane: "include" | "exclude",
    uiId: string,
    updater: (c: EarlyBirdRuleWithUiId["include"][0]) => EarlyBirdRuleWithUiId["include"][0]
): EarlyBirdRuleWithUiId {
    const arr = lane === "include" ? [...rule.include] : [...rule.exclude];
    const idx = arr.findIndex((c) => (c as any).uiId === uiId);
    if (idx === -1) return rule;
    arr[idx] = updater(arr[idx]);
    return lane === "include" ? { ...rule, include: arr } : { ...rule, exclude: arr };
}

export function reducer(
    state: EarlyBirdOfferBuilderState | undefined,
    action: Action
): EarlyBirdOfferBuilderState {
    if (!state) return startingState;

    switch (action.type) {
        case ActionType.PageLoaded:
            return {
                rules: action.rules,
                year: action.year,
                divisionId: action.divisionId,
                lookups: action.data,
                editingConditionUiId: null,
                dirty: false,
            };

        case ActionType.SetRules:
            return { ...state, rules: action.rules, dirty: true };

        case ActionType.AddRule: {
            const newRule: EarlyBirdRuleWithUiId = {
                name: "",
                earlyBirdEndDate: "",
                include: [],
                exclude: [],
            };
            return { ...state, rules: [...state.rules, newRule], dirty: true };
        }

        case ActionType.RemoveRule: {
            if (state.rules.length <= 1) {
                return {
                    ...state,
                    rules: [
                        {
                            name: "",
                            earlyBirdEndDate: "",
                            include: [],
                            exclude: [],
                        },
                    ],
                    editingConditionUiId: null,
                    dirty: true,
                };
            }
            const rules = state.rules.filter((_, i) => i !== action.ruleIndex);
            const removedRule = state.rules[action.ruleIndex];
            const wasEditingRemoved =
                removedRule &&
                state.editingConditionUiId &&
                [...removedRule.include, ...removedRule.exclude].some((c) => (c as any).uiId === state.editingConditionUiId);
            return {
                ...state,
                rules,
                editingConditionUiId: wasEditingRemoved ? null : state.editingConditionUiId,
                dirty: true,
            };
        }

        case ActionType.UpdateRule: {
            const rules = state.rules.map((r, i) =>
                i === action.ruleIndex ? { ...r, ...action.patch } : r
            );
            return { ...state, rules, dirty: true };
        }

        case ActionType.AddCondition: {
            const rules = state.rules.map((r, i) => {
                if (i !== action.ruleIndex) return r;
                const arr = action.lane === "include" ? [...r.include, action.condition] : [...r.exclude, action.condition];
                return action.lane === "include" ? { ...r, include: arr } : { ...r, exclude: arr };
            });
            return { ...state, rules, editingConditionUiId: action.condition.uiId, dirty: true };
        }

        case ActionType.RemoveCondition: {
            const rules = state.rules.map((r, i) => {
                if (i !== action.ruleIndex) return r;
                const arr = action.lane === "include"
                    ? r.include.filter((c) => (c as any).uiId !== action.uiId)
                    : r.exclude.filter((c) => (c as any).uiId !== action.uiId);
                return action.lane === "include" ? { ...r, include: arr } : { ...r, exclude: arr };
            });
            const editingConditionUiId = state.editingConditionUiId === action.uiId ? null : state.editingConditionUiId;
            return { ...state, rules, editingConditionUiId, dirty: true };
        }

        case ActionType.UpdateCondition: {
            const rules = state.rules.map((r, i) => {
                if (i !== action.ruleIndex) return r;
                return updateConditionInRule(r, action.lane, action.uiId, () => action.condition);
            });
            return { ...state, rules, dirty: true };
        }

        case ActionType.SetEditingConditionUiId:
            return { ...state, editingConditionUiId: action.uiId };

        default:
            return state;
    }
}